// web-next/src/features/upstream-auth/api.ts
export type UpstreamState =
  | { kind: "disabled"; reason: string }
  | { kind: "healthy"; expiresAt: string; healthySinceAt: string }
  | { kind: "reauth-needed"; lastErrorAt: string }
  | { kind: "reauth-in-progress"; driverEmail: string; startedAt: string };

export interface UpstreamSnapshot {
  state: UpstreamState;
  tokenExpiresAt: string | null;
  /** Server-clock timestamp at the moment the snapshot was produced.
   *  The store translates the other timestamps into client time using
   *  this so the UI's `Date.now()`-driven timers stay correct under
   *  clock skew. See `translateSnapshot` in `store.tsx`. */
  serverNow: string;
}

export interface EventsFrame {
  type: "upstream-state";
  snapshot: UpstreamSnapshot;
}

export type ReauthFrame =
  | { type: "frame"; b64: string; meta: { sessionId: string; timestamp: number } }
  | { type: "status"; status: { kind: "ready" } | { kind: "completed" } | { kind: "aborted"; reason: string } };

export type ReauthInput =
  | { type: "mouse"; event: "press" | "release" | "move"; x: number; y: number; button?: "left" | "middle" | "right"; modifiers?: number }
  | { type: "key"; event: "down" | "up" | "char"; code?: string; key?: string; text?: string; modifiers?: number }
  | { type: "wheel"; x: number; y: number; deltaX: number; deltaY: number }
  | { type: "paste"; text: string }
  | { type: "abort" };

// In dev (Vite on :5173) WebSockets bypass the proxy and talk to the
// backend on :8080 directly — Vite's bundled http-proxy crashes on
// Bun (socket.destroySoon is not implemented). Same idiom as the
// status-bar connection WS.
function wsHost(): string {
  return import.meta.env.DEV ? "localhost:8080" : location.host;
}

export function eventsWsUrl(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${wsHost()}/api/events`;
}

export function reauthWsUrl(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${wsHost()}/api/upstream-reauth/stream`;
}

export type StartReauthResult = { ok: true } | { error: "locked"; driverEmail: string } | { error: "disabled" };

// Client-side view of the interactive sign-in session shown in the modal.
export type ReauthStatus =
  | { kind: "connecting" }
  | { kind: "streaming" }
  | { kind: "completed" }
  | { kind: "ended"; reason: string }
  | { kind: "locked"; driverEmail: string }
  | { kind: "unavailable" };

export async function startReauth(): Promise<StartReauthResult> {
  const r = await fetch("/api/upstream-reauth/start", { method: "POST", credentials: "same-origin" });
  if (r.ok) return { ok: true };
  try { return (await r.json()) as Exclude<StartReauthResult, { ok: true }>; }
  catch { return { error: "disabled" }; }
}

export async function refreshToken(): Promise<{ ok: true } | { error: string }> {
  const r = await fetch("/api/upstream-refresh", { method: "POST", credentials: "same-origin" });
  if (r.ok) return { ok: true };
  try { return (await r.json()) as { error: string }; }
  catch { return { error: "request failed" }; }
}

export async function logoutSession(): Promise<{ ok: true } | { error: string }> {
  const r = await fetch("/api/upstream-logout", { method: "POST", credentials: "same-origin" });
  if (r.ok) return { ok: true };
  try { return (await r.json()) as { error: string }; }
  catch { return { error: "request failed" }; }
}
